import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { PROFILE_FULL_PATH } from "@src/appV2/redesign/Accounts/paths";
import { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { BadgeStep, ERROR_MESSAGES } from "../constants";
import type { BadgeFlowActions, BadgeFlowState } from "../types";
import {
  type ActiveQualificationWithLicenses,
  useActiveQualificationsWithLicenses,
} from "./useActiveQualificationsWithLicenses";

interface SelectedQualificationWithLicense {
  qualification: ActiveQualificationWithLicenses["qualification"];
  license?: ActiveQualificationWithLicenses["licenses"][0];
}

export function useBadgeFlow() {
  const [currentStep, setCurrentStep] = useState<BadgeStep>(BadgeStep.INFO);
  const [selectedQualificationId, setSelectedQualificationId] = useState<string | undefined>(
    undefined
  );
  const [selectedLicenseId, setSelectedLicenseId] = useState<string | undefined>(undefined);
  const [selectedQualificationWithLicense, setSelectedQualificationWithLicense] = useState<
    SelectedQualificationWithLicense | undefined
  >(undefined);

  const { data: activeQualifications, isLoading: isLoadingQualifications } =
    useActiveQualificationsWithLicenses();
  const { showErrorToast } = useToast();
  const location = useLocation();
  const history = useHistory();
  const badgeBottomSheetModalState = useModalState();

  // Derived values
  const hasNoActiveQualifications = activeQualifications.length === 0;
  const hasSingleQualification = activeQualifications.length === 1;
  const selectedQualification = activeQualifications.find(
    (q) => q.qualification.id === selectedQualificationId
  );
  const selectedQualificationHasMultipleLicenses = Boolean(
    selectedQualification?.licenses.length && selectedQualification.licenses.length > 1
  );

  const goBack = useCallback(() => {
    if (isDefined(location.key)) {
      history.goBack();
    } else {
      history.replace(PROFILE_FULL_PATH);
    }
  }, [location.key, history]);

  function openBadgeBottomSheet(
    qualification: ActiveQualificationWithLicenses["qualification"],
    license?: ActiveQualificationWithLicenses["licenses"][0]
  ) {
    setSelectedQualificationWithLicense({ qualification, license });
    badgeBottomSheetModalState.openModal();
  }

  async function handleViewBadge() {
    if (hasNoActiveQualifications) {
      showErrorToast(ERROR_MESSAGES.NO_ACTIVE_QUALIFICATIONS);
      logEvent(APP_V2_USER_EVENTS.NO_ROLE_OR_ACTIVE_LICENSES_FOR_BADGE_ERROR);
      return;
    }

    // If single qualification with single license, go directly to generate badge
    if (hasSingleQualification && activeQualifications[0].licenses.length <= 1) {
      const qualification = activeQualifications[0];
      openBadgeBottomSheet(qualification.qualification, qualification.licenses[0]);
      return;
    }

    // Otherwise start the selection flow
    if (hasSingleQualification) {
      setSelectedQualificationId(activeQualifications[0].qualification.id);
      if (activeQualifications[0].licenses.length > 1) {
        setCurrentStep(BadgeStep.STATE_SELECTION);
      } else {
        // No license case for auto-approved qualifications
        openBadgeBottomSheet(
          activeQualifications[0].qualification,
          activeQualifications[0].licenses[0]
        );
      }
    } else {
      setCurrentStep(BadgeStep.QUALIFICATION_SELECTION);
    }
  }

  function handleQualificationSelect(qualificationId: string) {
    setSelectedQualificationId(qualificationId);
  }

  function handleLicenseSelect(licenseId: string) {
    setSelectedLicenseId(licenseId);
  }

  async function handleContinueFromQualification() {
    const qualification = activeQualifications.find(
      (q) => q.qualification.id === selectedQualificationId
    );
    if (!qualification) {
      return;
    }

    if (qualification.licenses.length === 1) {
      setSelectedLicenseId(qualification.licenses[0].id);
      openBadgeBottomSheet(qualification.qualification, qualification.licenses[0]);
    } else if (qualification.licenses.length > 1) {
      setCurrentStep(BadgeStep.STATE_SELECTION);
    } else {
      // No licenses for this qualification (auto-approved)
      openBadgeBottomSheet(qualification.qualification, qualification.licenses[0]);
    }
  }

  async function handleContinueFromState() {
    const qualification = activeQualifications.find(
      (q) => q.qualification.id === selectedQualificationId
    );
    const license = qualification?.licenses.find((l) => l.id === selectedLicenseId);

    if (qualification && license) {
      openBadgeBottomSheet(qualification.qualification, license);
    }
  }

  function handleClickBack() {
    if (currentStep === BadgeStep.STATE_SELECTION) {
      setCurrentStep(BadgeStep.QUALIFICATION_SELECTION);
      setSelectedLicenseId(undefined);
    } else if (currentStep === BadgeStep.QUALIFICATION_SELECTION) {
      setCurrentStep(BadgeStep.INFO);
      setSelectedQualificationId(undefined);
      setSelectedLicenseId(undefined);
    } else {
      goBack();
    }
  }

  const state: BadgeFlowState = {
    currentStep,
    selectedQualificationId,
    selectedLicenseId,
  };

  const actions: BadgeFlowActions = {
    setCurrentStep,
    setSelectedQualificationId,
    setSelectedLicenseId,
    handleViewBadge,
    handleQualificationSelect,
    handleLicenseSelect,
    handleContinueFromQualification,
    handleContinueFromState,
    handleClickBack,
  };

  return {
    state,
    actions,
    activeQualifications,
    selectedQualification,
    selectedQualificationHasMultipleLicenses,
    isLoadingQualifications,
    badgeBottomSheetModalState,
    selectedQualificationWithLicense,
  };
}
